import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { Loader2, PlayCircle, ShieldCheck, ShieldAlert, Database } from 'lucide-react';

// Governed Grupo Novera demo-data migration console (hm_admin only).
// Reuses POST /api/admin/demo/novera-migration — NO backend changes.
const CONFIRM_TEXT = 'GRUPO NOVERA';

const isArtifact = (c) =>
  (c.part === 'survey' && String(c.error || '').includes('questionnaire missing')) ||
  (c.part === 'assignments' && String(c.error || '').includes('template missing')) ||
  (c.part === 'profiles' && c.existing_kept === true);

function buildGates(d) {
  const n = (p, k) => ((d.parts?.[p] || {})[k] || []).length;
  const conflicts = d.conflicts || [];
  const real = conflicts.filter((c) => !isArtifact(c));
  const tmplMissing = conflicts.filter((c) => c.part === 'assignments' && isArtifact(c)).length;
  const gates = [
    ['dry_run === true', d.dry_run === true],
    ['Cero conflictos sin resolver / zero unresolved conflicts', real.length === 0],
    ['Perfiles TalentHeart = 24', n('profiles', 'created') + n('profiles', 'exists') === 24],
    ['Cuestionario de clima planificado', n('questionnaire', 'created') + n('questionnaire', 'exists') >= 1],
    ['Reconocimientos = 12', n('recognitions', 'created') + n('recognitions', 'exists') === 12],
    ['Plantillas de onboarding = 2', n('templates', 'created') + n('templates', 'exists') === 2],
    ['Asignaciones de onboarding = 4', n('assignments', 'created') + n('assignments', 'exists') + tmplMissing === 4],
    ['Acciones de capacitación nuevas = 7', n('training', 'created') + n('training', 'exists') === 7],
    ['NOM-029 de Camila excluida (flag)', n('training', 'skipped') === 1],
    ['Designación de evaluador = 1', n('grant', 'created') + n('grant', 'exists') === 1],
  ];
  return { gates, real, artifacts: conflicts.filter(isArtifact), allPass: gates.every(([, ok]) => ok) };
}

function ManifestBlock({ title, data, testid }) {
  return (
    <div className="mt-4">
      <p className="text-sm font-semibold mb-1">{title}</p>
      <pre data-testid={testid} className="text-xs bg-slate-950 text-emerald-200 rounded-md p-3 overflow-auto max-h-96 whitespace-pre-wrap">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export default function AdminDemoMigration() {
  const { i18n } = useTranslation();
  const { user } = useApp();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [busy, setBusy] = useState(false);
  const [dry, setDry] = useState(null);
  const [applyRes, setApplyRes] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  if (user?.role !== 'hm_admin') {
    return (
      <div className="p-8" data-testid="demo-migration-denied">
        <p className="text-sm text-muted-foreground">{L('Acceso restringido a hm_admin.', 'Restricted to hm_admin.')}</p>
      </div>
    );
  }

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/demo/novera-migration', {
        dry_run: dryRun, include_camila_nom029: false,
      }, { timeout: 120000 });
      if (dryRun) { setDry(data); setApplyRes(null); toast.success(L('Dry-run completado — revisa los controles.', 'Dry-run complete — review the gates.')); }
      else { setApplyRes(data); toast.success(L('Migración aplicada.', 'Migration applied.')); }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      setConfirmText('');
    }
  };

  const g = dry ? buildGates(dry) : null;

  return (
    <div className="min-h-screen bg-background">
      <Topbar title={L('Migración demo — Grupo Novera', 'Demo migration — Grupo Novera')} />
      <div className="p-6 max-w-5xl space-y-4" data-testid="demo-migration-page">
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-teal-700" />
            <h2 className="text-lg font-semibold">{L('Consola gobernada de migración (idempotente, solo Grupo Novera)', 'Governed migration console (idempotent, Grupo Novera only)')}</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            {L('1) Ejecuta el dry-run (cero escrituras). 2) Revisa los controles y el manifiesto. 3) Aplica solo si todos los controles pasan. Nunca sobrescribe registros existentes; los datos sintéticos quedan marcados como demostración.',
               '1) Run the dry-run (zero writes). 2) Review gates and manifest. 3) Apply only if every gate passes. Existing records are never overwritten; synthetic data stays flagged as demo.')}
          </p>
          <div className="flex items-center gap-3">
            <Button onClick={() => run(true)} disabled={busy} data-testid="demo-migration-dryrun-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              {L('Ejecutar dry-run', 'Run dry-run')}
            </Button>
            <Button
              variant="destructive"
              disabled={busy || !g || !g.allPass || !!applyRes}
              onClick={() => setConfirmOpen(true)}
              data-testid="demo-migration-apply-btn"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {L('Aplicar migración', 'Apply migration')}
            </Button>
            {g && !g.allPass && (
              <Badge variant="outline" className="border-rose-300 text-rose-700" data-testid="demo-migration-gates-failed">
                {L('Controles fallidos — aplicación bloqueada', 'Gates failed — apply blocked')}
              </Badge>
            )}
          </div>
        </Card>

        {g && (
          <Card className="p-5" data-testid="demo-migration-gates-card">
            <h3 className="font-semibold mb-3">{L('Controles de seguridad', 'Safety gates')}</h3>
            <div className="space-y-1.5">
              {g.gates.map(([label, ok]) => (
                <div key={label} className="flex items-center gap-2 text-sm" data-testid={`gate-${ok ? 'pass' : 'fail'}`}>
                  {ok ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <ShieldAlert className="h-4 w-4 text-rose-600" />}
                  <span className={ok ? '' : 'text-rose-700 font-medium'}>{label}</span>
                  <Badge variant="outline" className={ok ? 'border-emerald-300 text-emerald-700' : 'border-rose-300 text-rose-700'}>{ok ? 'PASS' : 'FAIL'}</Badge>
                </div>
              ))}
            </div>
            {g.artifacts.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold">{L('Artefactos de dependencia del dry-run (se resuelven al aplicar)', 'Dry-run dependency artifacts (self-resolve on apply)')}</p>
                <pre className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 mt-1 overflow-auto max-h-40" data-testid="demo-migration-artifacts">{JSON.stringify(g.artifacts, null, 2)}</pre>
              </div>
            )}
            {g.real.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-rose-700">{L('Conflictos sin resolver — DETENTE y repórtalos', 'Unresolved conflicts — STOP and report them')}</p>
                <pre className="text-xs bg-rose-50 border border-rose-200 rounded-md p-2 mt-1 overflow-auto max-h-40" data-testid="demo-migration-conflicts">{JSON.stringify(g.real, null, 2)}</pre>
              </div>
            )}
            <ManifestBlock title={L('Manifiesto completo del dry-run', 'Full dry-run manifest')} data={dry} testid="demo-migration-dry-manifest" />
          </Card>
        )}

        {applyRes && (
          <Card className="p-5 border-emerald-300" data-testid="demo-migration-apply-card">
            <h3 className="font-semibold text-emerald-700 mb-2">{L('✔ Migración aplicada', '✔ Migration applied')}</h3>
            <ManifestBlock title={L('Resultado de la aplicación', 'Apply result')} data={applyRes} testid="demo-migration-apply-manifest" />
          </Card>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="demo-migration-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{L('Confirmar migración a PRODUCCIÓN', 'Confirm PRODUCTION migration')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {L(`Esto creará los registros de demostración aprobados para Grupo Novera en LIVE. Escribe ${CONFIRM_TEXT} para confirmar.`,
               `This will create the approved Grupo Novera demo records in LIVE. Type ${CONFIRM_TEXT} to confirm.`)}
          </p>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={CONFIRM_TEXT} data-testid="demo-migration-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="demo-migration-confirm-cancel">{L('Cancelar', 'Cancel')}</Button>
            <Button
              variant="destructive"
              disabled={confirmText.trim().toUpperCase() !== CONFIRM_TEXT || busy}
              onClick={() => run(false)}
              data-testid="demo-migration-confirm-apply"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {L('Aplicar ahora', 'Apply now')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
